import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pressMentionsApi, settingsApi, type PressMention } from "@/integrations/mysql/client";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical, Pencil, Trash2, Plus, Star, Eye, EyeOff,
  ExternalLink, Newspaper, Loader2, Save, X, Settings2, ChevronDown, ChevronUp,
} from "lucide-react";

// ── Vehicle type defaults ──────────────────────────────────────────────────────

const VEHICLE_TYPES_KEY = "press_vehicle_types";
const DEFAULT_VEHICLE_TYPES = ["TV", "Rádio", "Podcast", "Jornal", "Revista", "Online", "Instagram", "Outros"];

function parseVehicleTypes(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as string[];
  } catch { /* ignore */ }
  return DEFAULT_VEHICLE_TYPES;
}

// ── Vehicle type manager ───────────────────────────────────────────────────────

function VehicleTypeManager({ types, onSave }: { types: string[]; onSave: (t: string[]) => void }) {
  const [open, setOpen]   = useState(false);
  const [local, setLocal] = useState<string[]>(types);
  const [input, setInput] = useState("");
  const { toast }         = useToast();
  const qc                = useQueryClient();
  const [saving, setSaving] = useState(false);

  const add = () => {
    const v = input.trim();
    if (!v || local.includes(v)) return;
    setLocal(p => [...p, v]);
    setInput("");
  };

  const remove = (t: string) => setLocal(p => p.filter(x => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.update(VEHICLE_TYPES_KEY, JSON.stringify(local));
      await qc.invalidateQueries({ queryKey: ["site-settings"] });
      onSave(local);
      toast({ title: "Lista de tipos salva!" });
      setOpen(false);
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => { setLocal(types); setOpen(o => !o); }}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700"
      >
        <span className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-brand-primary" />
          Gerenciar tipos de veículo ({types.length})
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="p-4 space-y-3 bg-white">
          <p className="text-xs text-gray-400 leading-relaxed">
            Esses tipos aparecem no menu suspenso ao cadastrar uma menção. Adicione, reordene ou remova conforme necessário.
          </p>

          <div className="flex flex-wrap gap-2">
            {local.map(t => (
              <span key={t} className="flex items-center gap-1 text-xs font-medium bg-brand-primary/8 text-brand-primary px-2.5 py-1 rounded-full">
                {t}
                <button type="button" onClick={() => remove(t)} className="hover:text-red-500 transition-colors ml-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {local.length === 0 && <p className="text-xs text-gray-400 italic">Nenhum tipo cadastrado.</p>}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder="Novo tipo (ex: YouTube, Newsletter)"
              maxLength={80}
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
            />
            <button type="button" onClick={add}
              className="px-3 py-2 bg-brand-primary/10 text-brand-primary rounded-lg text-sm font-semibold hover:bg-brand-primary/20 transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={save} disabled={saving}
              className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Salvar lista
            </button>
            <button type="button" onClick={() => setOpen(false)}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

type FormData = {
  publication_name: string;
  publication_logo_url: string;
  cover_image_url: string;
  title: string;
  excerpt: string;
  url: string;
  published_at: string;
  category: string;
  subject: string;
  is_featured: boolean;
  is_active: boolean;
  display_order: number;
};

const EMPTY_FORM: FormData = {
  publication_name: "", publication_logo_url: "", cover_image_url: "", title: "",
  excerpt: "", url: "", published_at: "", category: "", subject: "",
  is_featured: false, is_active: true, display_order: 0,
};

// ── Field wrapper ──────────────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all";

// ── Mention form ───────────────────────────────────────────────────────────────

function MentionForm({
  initial, onSave, onCancel, saving, vehicleTypes,
}: {
  initial: FormData;
  onSave: (data: FormData) => void;
  onCancel: () => void;
  saving: boolean;
  vehicleTypes: string[];
}) {
  const [form, setForm] = useState<FormData>(initial);
  const set = (key: keyof FormData, val: unknown) => setForm(p => ({ ...p, [key]: val }));
  const valid = form.publication_name.length >= 2 && form.title.length >= 3 && form.url.startsWith("http");

  return (
    <div className="bg-white border-2 border-brand-primary/20 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-800">
          {initial.title ? "Editar Menção" : "Nova Menção de Imprensa"}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Veículo de Comunicação" required>
          <input type="text" value={form.publication_name} onChange={e => set("publication_name", e.target.value)}
            placeholder="Ex: Folha de S.Paulo, G1, Veja Saúde" maxLength={200} className={inputCls} />
        </Field>

        <Field label="Tipo de Veículo">
          <select value={form.category} onChange={e => set("category", e.target.value)} className={inputCls}>
            <option value="">— Selecione —</option>
            {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Título do Artigo" required>
        <input type="text" value={form.title} onChange={e => set("title", e.target.value)}
          placeholder="Título da matéria ou artigo" maxLength={500} className={inputCls} />
      </Field>

      <Field label="Assunto / Tema">
        <input type="text" value={form.subject} onChange={e => set("subject", e.target.value)}
          placeholder="Ex: Nutrição Esportiva, Emagrecimento, Gestação" maxLength={300} className={inputCls} />
        <p className="text-[11px] text-gray-400 mt-1">Use para facilitar filtros no site. Pode colocar mais de um separado por vírgula.</p>
      </Field>

      <Field label="Resumo (opcional)">
        <textarea value={form.excerpt} onChange={e => set("excerpt", e.target.value)}
          placeholder="Breve descrição do conteúdo..." rows={3} maxLength={500} className={`${inputCls} resize-none`} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Link do Artigo" required>
          <input type="url" value={form.url} onChange={e => set("url", e.target.value)}
            placeholder="https://..." className={inputCls} />
        </Field>

        <Field label="Data de Publicação">
          <input type="date" value={form.published_at} onChange={e => set("published_at", e.target.value)} className={inputCls} />
        </Field>
      </div>

      <Field label="Capa da Matéria (opcional)">
        <p className="text-[11px] text-gray-400 mb-2">Imagem de capa da matéria exibida no card do site. Recomendado: proporção 16:9 ou 3:2.</p>
        <PhotoCropUpload
          value={form.cover_image_url}
          onChange={url => set("cover_image_url", url)}
          shape="rect"
          aspect={16 / 9}
          label="Capa da matéria"
          previewClassName="w-full max-w-sm aspect-[4/3] object-cover rounded-xl border border-gray-200"
        />
      </Field>

      <Field label="Logo do Veículo (opcional)">
        <PhotoCropUpload
          value={form.publication_logo_url}
          onChange={url => set("publication_logo_url", url)}
          shape="square"
          aspect={1}
          label="Logo do veículo"
          previewClassName="w-14 h-14 object-contain rounded-xl border border-gray-100 bg-gray-50"
        />
      </Field>

      <div className="flex items-center gap-6 pt-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <button
            type="button"
            onClick={() => set("is_featured", !form.is_featured)}
            className={`w-10 h-5 rounded-full transition-colors relative ${form.is_featured ? "bg-amber-400" : "bg-gray-200"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_featured ? "translate-x-5" : "translate-x-0"}`} />
          </button>
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <Star className={`w-3.5 h-3.5 ${form.is_featured ? "text-amber-400 fill-current" : "text-gray-300"}`} />
            Destaque
          </span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <button
            type="button"
            onClick={() => set("is_active", !form.is_active)}
            className={`w-10 h-5 rounded-full transition-colors relative ${form.is_active ? "bg-brand-primary" : "bg-gray-200"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-5" : "translate-x-0"}`} />
          </button>
          <span className="text-sm text-gray-600">Visível no site</span>
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave(form)} disabled={!valid || saving}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar
        </button>
        <button onClick={onCancel} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Sortable row ───────────────────────────────────────────────────────────────

function SortableMentionRow({
  mention, onEdit, onDelete, onToggle, onToggleFeatured, disabled,
}: {
  mention: PressMention;
  onEdit: (m: PressMention) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, cur: boolean) => void;
  onToggleFeatured: (id: string, cur: boolean) => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: mention.id });

  return (
    <div ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="bg-white border border-gray-100 rounded-xl p-3 flex items-start gap-3"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1 text-gray-300 hover:text-gray-500 shrink-0 touch-none">
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Logo / initials */}
      <div className="w-12 h-12 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
        {mention.publication_logo_url ? (
          <img src={mention.publication_logo_url} alt={mention.publication_name} className="w-full h-full object-contain p-1" />
        ) : (
          <span className="text-xs font-bold text-brand-primary/60">
            {mention.publication_name.slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-xs font-bold text-brand-primary">{mention.publication_name}</span>
          {mention.category && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-brand-primary/8 text-brand-primary rounded-full">
              {mention.category}
            </span>
          )}
          {!!mention.is_featured && <Star className="w-3 h-3 text-amber-400 fill-current" />}
          {mention.published_at && (
            <span className="text-[10px] text-gray-400 ml-auto">{fmtDate(mention.published_at)}</span>
          )}
        </div>
        <p className="text-sm font-semibold text-gray-800 line-clamp-1">{mention.title}</p>
        {mention.excerpt && <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{mention.excerpt}</p>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onToggleFeatured(mention.id, mention.is_featured)} title="Alternar destaque"
          className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-colors ${mention.is_featured ? "border-amber-200 bg-amber-50 text-amber-400" : "border-gray-200 text-gray-300 hover:border-amber-200 hover:text-amber-400"}`}>
          <Star className="w-3.5 h-3.5 fill-current" />
        </button>
        <button onClick={() => onToggle(mention.id, mention.is_active)}
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${mention.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>
          {mention.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {mention.is_active ? "Visível" : "Oculto"}
        </button>
        <button onClick={() => onEdit(mention)} disabled={disabled}
          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-brand-primary hover:border-brand-primary/30 transition-colors disabled:opacity-30">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(mention.id)}
          className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ImprensaManager() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { getSetting } = useSiteSettings();
  const [vehicleTypes, setVehicleTypes] = useState<string[]>(
    parseVehicleTypes(getSetting(VEHICLE_TYPES_KEY, JSON.stringify(DEFAULT_VEHICLE_TYPES)))
  );

  const { data: mentions = [], isLoading } = useQuery({
    queryKey: ["press-mentions"],
    queryFn: () => pressMentionsApi.list(false),
  });

  const [localItems, setLocalItems] = useState<PressMention[] | null>(null);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteId, setDeleteId]     = useState<string | null>(null);
  const [saving, setSaving]         = useState(false);

  const displayed = localItems ?? mentions;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const refresh = () => { qc.invalidateQueries({ queryKey: ["press-mentions"] }); setLocalItems(null); };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = displayed.findIndex(m => m.id === active.id);
    const to   = displayed.findIndex(m => m.id === over.id);
    const reordered = arrayMove(displayed, from, to);
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((m, i) => pressMentionsApi.update(m.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao reordenar", variant: "destructive" });
      setLocalItems(null);
    }
  };

  const handleSave = async (form: FormData) => {
    setSaving(true);
    try {
      const editing = displayed.find(m => m.id === editingId);
      if (editingId && editing) {
        await pressMentionsApi.update(editingId, {
          ...form,
          publication_logo_url: form.publication_logo_url || null,
          cover_image_url: form.cover_image_url || null,
          excerpt:      form.excerpt      || null,
          published_at: form.published_at || null,
          category:     form.category     || null,
          subject:      form.subject      || null,
        });
        toast({ title: "Menção atualizada!" });
      } else {
        await pressMentionsApi.create({
          ...form,
          publication_logo_url: form.publication_logo_url || null,
          cover_image_url: form.cover_image_url || null,
          excerpt:      form.excerpt      || null,
          published_at: form.published_at || null,
          category:     form.category     || null,
          subject:      form.subject      || null,
          display_order: displayed.length,
        });
        toast({ title: "Menção criada!" });
      }
      setEditingId(null);
      setIsCreating(false);
      refresh();
    } catch (e: any) {
      toast({ title: e.message || "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await pressMentionsApi.delete(id);
      toast({ title: "Menção excluída." });
      refresh();
    } catch {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleToggle = async (id: string, cur: boolean) => {
    await pressMentionsApi.update(id, { is_active: !cur });
    refresh();
  };

  const handleToggleFeatured = async (id: string, cur: boolean) => {
    await pressMentionsApi.update(id, { is_featured: !cur });
    refresh();
  };

  const editingMention = displayed.find(m => m.id === editingId);
  const formInitial: FormData = editingMention
    ? {
        publication_name: editingMention.publication_name,
        publication_logo_url: editingMention.publication_logo_url ?? "",
        cover_image_url: editingMention.cover_image_url ?? "",
        title: editingMention.title,
        excerpt: editingMention.excerpt ?? "",
        url: editingMention.url,
        published_at: editingMention.published_at ?? "",
        category: editingMention.category ?? "",
        subject: editingMention.subject ?? "",
        is_featured: editingMention.is_featured,
        is_active: editingMention.is_active,
        display_order: editingMention.display_order,
      }
    : { ...EMPTY_FORM, display_order: displayed.length };

  if (isLoading) return <div className="py-10 text-center text-sm text-gray-400">Carregando...</div>;

  return (
    <div className="space-y-4">
      <PageVisibilityToggle pageKey="page_imprensa_visible" label="Página de Imprensa" />

      <div className="flex justify-end">
        <button
          onClick={() => { setIsCreating(true); setEditingId(null); }}
          disabled={isCreating || !!editingId}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Nova Menção
        </button>
      </div>

      <VehicleTypeManager types={vehicleTypes} onSave={setVehicleTypes} />

      {(isCreating || editingId) && (
        <MentionForm
          initial={formInitial}
          onSave={handleSave}
          onCancel={() => { setIsCreating(false); setEditingId(null); }}
          saving={saving}
          vehicleTypes={vehicleTypes}
        />
      )}

      {displayed.length === 0 && !isCreating ? (
        <div className="flex flex-col items-center justify-center py-14 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
            <Newspaper className="w-6 h-6 text-gray-300" />
          </div>
          <p className="text-gray-500 text-sm font-medium">Nenhuma menção cadastrada</p>
          <p className="text-gray-400 text-xs mt-1">Clique em "Nova Menção" para adicionar a primeira aparição na mídia.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <GripVertical className="w-3.5 h-3.5" />
            Arraste para reordenar · {displayed.length} menção{displayed.length !== 1 ? "ões" : ""}
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayed.map(m => m.id)} strategy={verticalListSortingStrategy}>
              {displayed.map(m => (
                <SortableMentionRow
                  key={m.id}
                  mention={m}
                  onEdit={m => { setEditingId(m.id); setIsCreating(false); }}
                  onDelete={id => setDeleteId(id)}
                  onToggle={handleToggle}
                  onToggleFeatured={handleToggleFeatured}
                  disabled={isCreating || !!editingId}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir menção</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteId) { handleDelete(deleteId); setDeleteId(null); } }}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
